import React, { useEffect, useState, useRef } from 'react';
import { CircleIcon, MinusIcon, DotIcon, HexagonIcon, XIcon, PlusIcon, SettingsIcon, MapIcon, GlobeIcon, RulerIcon, ChevronUpIcon, TypeIcon, TextCursorInputIcon } from 'lucide-react';
type TabType = 'insert' | 'draw' | 'topography' | 'analyse' | 'view' | 'tools';
type BaseMapSource = 'none' | 'google-satellite' | 'bing-maps';
type UnitSystem = 'metric' | 'us-standard';
const BASE_MAP_OPTIONS: {
  id: BaseMapSource;
  label: string;
}[] = [{
  id: 'none',
  label: 'None'
}, {
  id: 'google-satellite',
  label: 'Google Satellite'
}, {
  id: 'bing-maps',
  label: 'Bing Maps'
}];
const TABS: {
  id: TabType;
  label: string;
}[] = [{
  id: 'insert',
  label: 'Insert'
}, {
  id: 'draw',
  label: 'Draw'
}, {
  id: 'topography',
  label: 'Topography'
}, {
  id: 'analyse',
  label: 'Analyse'
}, {
  id: 'view',
  label: 'View'
}, {
  id: 'tools',
  label: 'Tools'
}];
export function ICADPage({
  onNavigate
}: {
  onNavigate?: (page: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<TabType>('insert');
  const [baseMap, setBaseMap] = useState<BaseMapSource>('none');
  const [baseMapMenuOpen, setBaseMapMenuOpen] = useState(false);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
  const baseMapRef = useRef<HTMLDivElement>(null);
  const [versions, setVersions] = useState([{
    id: 1,
    name: 'Version 1'
  }, {
    id: 2,
    name: 'Version 2'
  }]);
  const [activeVersion, setActiveVersion] = useState(1);
  const [nextVersionId, setNextVersionId] = useState(3);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (baseMapRef.current && !baseMapRef.current.contains(event.target as Node)) {
        setBaseMapMenuOpen(false);
      }
    }
    if (baseMapMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [baseMapMenuOpen]);
  const currentBaseMapLabel = BASE_MAP_OPTIONS.find(o => o.id === baseMap)?.label || 'None';
  const handleToolClick = (toolName: string) => {
    console.log(`Tool clicked: ${toolName}`);
  };
  const handleClose = () => {
    if (onNavigate) onNavigate('home');
  };
  const handleAddVersion = () => {
    const newVersion = {
      id: nextVersionId,
      name: `Version ${nextVersionId}`
    };
    setVersions([...versions, newVersion]);
    setActiveVersion(nextVersionId);
    setNextVersionId(nextVersionId + 1);
  };
  const handleRemoveVersion = (id: number) => {
    if (versions.length <= 1) return;
    const filtered = versions.filter(v => v.id !== id);
    setVersions(filtered);
    if (activeVersion === id) {
      setActiveVersion(filtered[filtered.length - 1].id);
    }
  };
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 overflow-hidden" data-id="element-9437">
      {/* Project Header + Tab Bar */}
      <div className="bg-[#0b1628] flex-shrink-0" data-id="element-9438">
        <div className="flex items-stretch" data-id="element-9439">
          {/* Logo — spans full height */}
          <div className="flex items-center px-4" data-id="element-9440">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cZrMyYGysL7x2nzbnxUSuo/White_Drop_for_Whatsapp.png" alt="Aisco" className="h-8 w-auto" data-id="element-9441" />
          </div>

          {/* Right side: project name + tabs stacked */}
          <div className="flex-1 flex flex-col min-w-0" data-id="element-9442">
            {/* Project name row */}
            <div className="flex items-center justify-between px-4 h-[34px]" data-id="element-9443">
              <span className="text-[#F1F5FB] text-sm font-semibold" data-id="element-9444">
                Las Turrazas II
              </span>
              <button onClick={handleClose} className="text-[#8A9BBA] hover:text-[#F87171] hover:bg-[rgba(239,68,68,0.15)] p-1 rounded transition-colors" title="Close iCAD" data-id="element-9445">
                <XIcon size={16} data-id="element-9446" />
              </button>
            </div>

            {/* Tabs row */}
            <div className="flex px-2" data-id="element-9447">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-1.5 text-xs font-medium transition-colors rounded-t-md ${activeTab === tab.id ? 'text-[#F1F5FB] bg-[#132038]' : 'text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5'}`} data-id="element-9448">
                  {tab.label}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Ribbon Content — lighter panel matching active tab */}
      <div className="bg-[#132038] border-b border-white/10 px-4 flex-shrink-0" data-id="element-9449">
        {activeTab === 'insert' && <div className="flex items-stretch" data-id="element-9450">
            {/* Pivots Section */}
            <RibbonSection label="Pivots" data-id="element-9451">
              <RibbonTool icon={<PivotFullCircleIcon data-id="element-9453" />} label="Full Circle" onClick={() => handleToolClick('Pivot Full Circle')} data-id="element-9452" />
              <RibbonTool icon={<PivotPartCircleIcon data-id="element-9455" />} label="Part Circle" onClick={() => handleToolClick('Pivot Part Circle')} data-id="element-9454" />
            </RibbonSection>

            {/* Lineals Section */}
            <RibbonSection label="Linears" data-id="element-9456">
              <RibbonTool icon={<LinealEndFeedIcon data-id="element-9458" />} label="EndFeed" onClick={() => handleToolClick('Lineal EndFeed')} data-id="element-9457" />
              <RibbonTool icon={<LinealCenterFeedIcon data-id="element-9460" />} label="Center Feed" onClick={() => handleToolClick('Lineal Center Feed')} data-id="element-9459" />
              <RibbonTool icon={<LinealSwingAroundIcon data-id="element-9462" />} label="Swing Around" onClick={() => handleToolClick('Lineal Swing Around')} data-id="element-9461" />
            </RibbonSection>

            {/* Boundaries Section */}
            <RibbonSection label="Boundaries" data-id="element-9463">
              <RibbonTool icon={<FarmBoundaryIcon data-id="element-9465" />} label="Farm Boundary" onClick={() => handleToolClick('Farm Boundary')} data-id="element-9464" />
            </RibbonSection>
          </div>}

        {activeTab === 'draw' && <div className="flex items-stretch" data-id="element-9466">
            {/* Lines Section */}
            <RibbonSection label="Lines" data-id="element-9467">
              <RibbonTool icon={<LineIcon data-id="element-9469" />} label="Line" onClick={() => handleToolClick('Line')} data-id="element-9468" />
              <RibbonTool icon={<PolylineIcon data-id="element-9471" />} label="Polyline" onClick={() => handleToolClick('Polyline')} data-id="element-9470" />
              <RibbonTool icon={<DrawArcIcon data-id="element-9473" />} label="Arc" onClick={() => handleToolClick('Arc')} data-id="element-9472" />
            </RibbonSection>

            {/* Shapes Section */}
            <RibbonSection label="Shapes" data-id="element-9474">
              <RibbonTool icon={<PointIcon data-id="element-9476" />} label="Point" onClick={() => handleToolClick('Point')} data-id="element-9475" />
              <RibbonTool icon={<DrawCircleIcon data-id="element-9478" />} label="Circle" onClick={() => handleToolClick('Circle')} data-id="element-9477" />
              <RibbonTool icon={<PolygonIcon data-id="element-9480" />} label="Polygon" onClick={() => handleToolClick('Polygon')} data-id="element-9479" />
            </RibbonSection>

            {/* Text Section */}
            <RibbonSection label="Text" data-id="element-9481">
              <RibbonTool icon={<TypeIcon size={24} data-id="element-9483" />} label="Text" onClick={() => handleToolClick('Text')} data-id="element-9482" />
              <RibbonTool icon={<TextCursorInputIcon size={24} data-id="element-9485" />} label="Text Box" onClick={() => handleToolClick('Text Box')} data-id="element-9484" />
            </RibbonSection>
          </div>}

        {activeTab === 'topography' && <div className="flex items-center justify-center py-4" data-id="element-9486">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9487">
              Topography tools coming soon
            </p>
          </div>}

        {activeTab === 'analyse' && <div className="flex items-center justify-center py-4" data-id="element-9488">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9489">Analysis tools coming soon</p>
          </div>}

        {activeTab === 'view' && <div className="flex items-center justify-center py-4" data-id="element-9490">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9491">View tools coming soon</p>
          </div>}

        {activeTab === 'tools' && <div className="flex items-center justify-center py-4" data-id="element-9492">
            <p className="text-[#8A9BBA] text-sm" data-id="element-9493">
              Additional tools coming soon
            </p>
          </div>}
      </div>

      {/* Canvas Area */}
      <div className="flex-1 relative overflow-hidden cursor-crosshair" data-id="element-9494">
        {/* Satellite base map */}
        {baseMap !== 'none' && <img src="https://cdn.magicpatterns.com/uploads/bzDbHYffoVA6xsN1KLaZzL/image.png" alt="Satellite base map" className="absolute inset-0 w-full h-full object-cover" draggable={false} data-id="element-9495" />}

        {/* Blank canvas background */}
        {baseMap === 'none' && <div className="absolute inset-0 bg-[#e8e8e8]" data-id="element-9496" />}

        {/* Crosshair indicator */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-9497">
          <div className={`w-8 h-0.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${baseMap !== 'none' ? 'bg-white/40' : 'bg-gray-400/30'}`} data-id="element-9498"></div>
          <div className={`h-8 w-0.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${baseMap !== 'none' ? 'bg-white/40' : 'bg-gray-400/30'}`} data-id="element-9499"></div>
        </div>

        {/* Canvas placeholder text */}
        <div className={`absolute top-8 left-8 text-sm ${baseMap !== 'none' ? 'text-white/60' : 'text-gray-400'}`} data-id="element-9500">
          <p className="text-[#F1F5FB]" data-id="element-9501">Drawing Canvas</p>
          <p className="text-xs mt-1 text-[#8A9BBA]" data-id="element-9502">
            Click tools in the ribbon to start
          </p>
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="bg-[#0B1628] border-t border-[#2F4268] flex-shrink-0 h-[36px] flex items-center justify-between px-2" data-id="element-9503">
        {/* Version Tabs — left */}
        <div className="flex items-center gap-0 overflow-x-auto no-scrollbar h-full" data-id="element-9504">
          {versions.map(version => <div key={version.id} onClick={() => setActiveVersion(version.id)} className={`flex items-center gap-1.5 px-3 h-full cursor-pointer transition-colors text-xs whitespace-nowrap relative ${activeVersion === version.id ? 'bg-[#1C2B47] text-[#F1F5FB] font-semibold' : 'text-[#C5D1E4] font-medium hover:text-[#F1F5FB] hover:bg-[#1C2B47]/50'}`} data-id="element-9505">
              {activeVersion === version.id && <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#00D4FF]" data-id="element-9506" />}
              <span data-id="element-9507">{version.name}</span>
              {versions.length > 1 && <button onClick={e => {
            e.stopPropagation();
            handleRemoveVersion(version.id);
          }} className="text-[#8A9BBA] hover:text-white hover:bg-[#2A3B5C] transition-colors p-0.5 -mr-1 rounded" data-id="element-9508">
                  <XIcon size={10} data-id="element-9509" />
                </button>}
            </div>)}
          <button onClick={handleAddVersion} className="text-[#8A9BBA] hover:text-[#00D4FF] hover:bg-[#2A3B5C] p-1 rounded transition-colors ml-1" title="Add new version" data-id="element-9510">
            <PlusIcon size={14} data-id="element-9511" />
          </button>
        </div>

        {/* Right side: Base Map | Projection | Units | Coordinates */}
        <div className="flex items-center gap-0.5 flex-shrink-0 ml-4" data-id="element-9512">
          {/* Base Map Selector */}
          <div className="relative" ref={baseMapRef} data-id="element-9513">
            <button onClick={() => setBaseMapMenuOpen(!baseMapMenuOpen)} className="flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Base map source" data-id="element-9514">
              <MapIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9515" />
              <span data-id="element-9516">{currentBaseMapLabel}</span>
              <ChevronUpIcon size={10} className={`transition-transform duration-150 ${baseMapMenuOpen ? 'rotate-180' : ''}`} data-id="element-9517" />
            </button>

            {/* Dropdown menu — opens upward */}
            {baseMapMenuOpen && <div className="absolute bottom-full left-0 mb-1 bg-[#0f1d3a] rounded-lg border border-white/10 shadow-2xl overflow-hidden min-w-[160px] z-50" data-id="element-9518">
                {BASE_MAP_OPTIONS.map(option => <button key={option.id} onClick={() => {
              setBaseMap(option.id);
              setBaseMapMenuOpen(false);
            }} className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium transition-colors text-left ${baseMap === option.id ? 'bg-white/10 text-white' : 'text-blue-200/70 hover:bg-white/5 hover:text-white'}`} data-id="element-9519">
                    {baseMap === option.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" data-id="element-9520" />}
                    {baseMap !== option.id && <div className="w-1.5 h-1.5 flex-shrink-0" data-id="element-9521" />}
                    <span data-id="element-9522">{option.label}</span>
                  </button>)}
              </div>}
          </div>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9523" />

          {/* Projection */}
          <button className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Coordinate Reference System" data-id="element-9524">
            <GlobeIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9525" />
            <span data-id="element-9526">CRS:84</span>
          </button>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9527" />

          {/* Unit System */}
          <button onClick={() => setUnitSystem(unitSystem === 'metric' ? 'us-standard' : 'metric')} className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium text-[#C5D1E4] hover:text-[#F1F5FB] hover:bg-white/5 transition-colors group" title="Toggle unit system" data-id="element-9528">
            <RulerIcon size={13} className="opacity-80 group-hover:opacity-100" data-id="element-9529" />
            <span data-id="element-9530">{unitSystem === 'metric' ? 'Metric' : 'US Standard'}</span>
          </button>

          {/* Separator */}
          <div className="w-px h-4 bg-white/10 mx-1" data-id="element-9531" />

          {/* Coordinates */}
          <span className="text-[#C5D1E4] text-[11px] font-mono px-1" data-id="element-9532">
            80.3455 ; 23.4899
          </span>
        </div>
      </div>
    </div>;
}
function RibbonSection({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col border-r border-white/10 last:border-r-0" data-id="element-9533">
      {/* Tools row */}
      <div className="flex gap-0.5 px-2 pt-2 pb-1.5 flex-1" data-id="element-9534">{children}</div>
      {/* Section label */}
      <div className="border-t border-white/[0.06] px-2 py-1" data-id="element-9535">
        <span className="text-[10px] text-[#8A9BBA] font-medium tracking-wide text-center block uppercase" data-id="element-9536">
          {label}
        </span>
      </div>
    </div>;
}
function RibbonTool({
  icon,
  label,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="flex flex-col items-center justify-center px-2.5 py-1 rounded hover:bg-white/10 transition-colors group min-w-[60px]" data-id="element-9537">
      <div className="text-[#C5D1E4] group-hover:text-[#F1F5FB] transition-colors mb-0.5" data-id="element-9538">
        {icon}
      </div>
      <span className="text-[#C5D1E4] group-hover:text-[#F1F5FB] transition-colors text-center text-[10px] leading-tight" data-id="element-9539">
        {label}
      </span>
    </button>;
}
// Custom SVG Icons for Irrigation Machines
function PivotFullCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/t426MnDWi6vKjFroEFiiEG/pivot_full_circle.svg" alt="Pivot Full Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9540" />;
}
function PivotPartCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/vJGELGZ5TYT3TmRbBxZn4K/pivot_part_circle.svg" alt="Pivot Part Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9541" />;
}
function LinealEndFeedIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/d6Rj7H3BJJpaY4ruiNQFqN/lineal_end_feed.svg" alt="Lineal EndFeed" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9542" />;
}
function LinealCenterFeedIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/n6vjXYtJr6nPr7QKMk8SnZ/center_feed.svg" alt="Lineal Center Feed" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9543" />;
}
function LinealSwingAroundIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/wjVXtWu3bButTsLqniBh5H/swing_around.svg" alt="Swing Around" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9544" />;
}
function FarmBoundaryIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/rpr57N2xXozSKDxU4Etw92/farm_boundary.svg" alt="Farm Boundary" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9545" />;
}
function PolylineIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/e3BxfS7zZq2ejBPrgvzuqV/polyline.svg" alt="Polyline" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9546" />;
}
function ArcIcon() {
  return <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" data-id="element-9547">
      <path d="M 4 16 Q 12 4 20 16" strokeWidth="2" fill="none" data-id="element-9548" />
      <circle cx="4" cy="16" r="1.5" fill="currentColor" data-id="element-9549" />
      <circle cx="20" cy="16" r="1.5" fill="currentColor" data-id="element-9550" />
    </svg>;
}
function LineIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/3rn8zQqNKSCCRuFaxiwtrt/line.svg" alt="Line" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9551" />;
}
function DrawArcIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/ipKcENdRXvKgjo7TJ5WjD4/arc.svg" alt="Arc" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9552" />;
}
function PointIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/irw4Jimuo9s35j33KJs4o7/point.svg" alt="Point" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9553" />;
}
function DrawCircleIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/qRfyeFFE35CpHYRoVg6GDq/circle.svg" alt="Circle" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9554" />;
}
function PolygonIcon() {
  return <img src="https://cdn.magicpatterns.com/uploads/qVQz9RaTSRThGRRUCLxZ9K/polygon.svg" alt="Polygon" width={34} height={34} className="filter brightness-0 invert opacity-80" draggable={false} data-id="element-9555" />;
}